// src/pages/DashboardContabile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const API_BASE = '/api';

// --- utils --------------------------------------------------
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  if (Number.isNaN(num)) return 0;
  return num;
};

const formatCurrencyLabel = (value) =>
  value.toLocaleString('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + ' €';

const formatDateMonthKey = (iso) => {
  if (!iso) return 'N/D';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'N/D';
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${year}-${month}`;
};

const formatMonthLabel = (key) => {
  if (!key || key.length < 7) return key || '';
  const [year, month] = key.split('-');
  const mesi = [
    'Gen',
    'Feb',
    'Mar',
    'Apr',
    'Mag',
    'Giu',
    'Lug',
    'Ago',
    'Set',
    'Ott',
    'Nov',
    'Dic',
  ];
  const mIndex = Number(month) - 1;
  const label = mesi[mIndex] || month;
  return `${label} ${year}`;
};

const PAYMENT_COLORS = {
  'Da fatturare': '#f97316',
  Fatturato: '#22c55e',
  Pagato: '#16a34a',
  Stornato: '#ef4444',
  Altro: '#6b7280',
};

const PIE_COLORS = ['#22c55e', '#f97316', '#16a34a', '#ef4444', '#6b7280'];

const DashboardContabile = () => {
  const navigate = useNavigate();
  const [role] = useState(localStorage.getItem('role') || null);

  const canAccess =
    role === 'admin' || role === 'gestione' || role === 'segreteria';

  useEffect(() => {
    if (!canAccess) navigate('/');
  }, [canAccess, navigate]);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filtri
  const [yearFilter, setYearFilter] = useState('tutti');
  const [customerFilter, setCustomerFilter] = useState('tutti');
  const [viewMode, setViewMode] = useState('12m'); // '12m' | 'year'

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/work/billing`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nel caricamento dati contabili.');
      }
      const data = await res.json();
      setOrders(data || []);
    } catch (err) {
      console.error('Errore DashboardContabile loadData:', err);
      setError(err.message || 'Errore imprevisto nel caricamento.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // --- applico i filtri base --------------------------------
  const filteredOrders = useMemo(() => {
    let list = orders;

    if (yearFilter !== 'tutti') {
      list = list.filter((o) => {
        const d = new Date(o.createdAt);
        if (Number.isNaN(d.getTime())) return false;
        return String(d.getFullYear()) === yearFilter;
      });
    }

    if (customerFilter !== 'tutti') {
      list = list.filter((o) => o.companyName === customerFilter);
    }

    return list;
  }, [orders, yearFilter, customerFilter]);

  // --- anni disponibili per filtro ---------------------------
  const yearOptions = useMemo(() => {
    const years = new Set();
    orders.forEach((o) => {
      if (!o.createdAt) return;
      const d = new Date(o.createdAt);
      if (!Number.isNaN(d.getTime())) years.add(d.getFullYear());
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [orders]);

  // --- clienti per filtro ------------------------------------
  const customerOptions = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => {
      if (o.companyName) set.add(o.companyName);
    });
    return Array.from(set).sort();
  }, [orders]);

  // --- STATISTICHE BASE --------------------------------------
  const {
    totalRevenue,
    totalPreventivi,
    totalOrdini,
    countOrders,
    avgTicket,
    monthlyData,
    paymentDistribution,
    topClientsRevenue,
    topClientsCount,
    marginData,
    daFatturareSum,
    fatturatoNonPagatoSum,
    cashPotentialTotal,
  } = useMemo(() => {
    let totalRevenue_ = 0;
    let totalPrev_ = 0;
    let totalOrd_ = 0;

    let daFatturareSum_ = 0;
    let fatturatoNonPagatoSum_ = 0;

    const monthlyMap = {};
    const paymentMap = {};
    const clientRevenueMap = {};
    const clientCountMap = {};
    const marginMap = {};

    // range per il grafico mensile
    const now = new Date();
    let chartStart = null;
    let chartEnd = null;

    if (viewMode === '12m') {
      chartEnd = now;
      chartStart = new Date(now);
      chartStart.setMonth(chartStart.getMonth() - 11); // 12 mesi inclusivi
      chartStart.setDate(1);
    } else {
      // viewMode === 'year'
      if (yearFilter !== 'tutti') {
        const y = Number(yearFilter);
        chartStart = new Date(y, 0, 1);
        chartEnd = new Date(y, 11, 31, 23, 59, 59);
      } else {
        const y = now.getFullYear();
        chartStart = new Date(y, 0, 1);
        chartEnd = new Date(y, 11, 31, 23, 59, 59);
      }
    }

    filteredOrders.forEach((o) => {
      const b = o.billing || {};
      const cons = formatCurrency(b.importoConsuntivo);
      const prev = formatCurrency(b.importoPreventivo);
      const ord = formatCurrency(b.importoOrdine);
      const stato = b.stato || 'Altro';

      totalRevenue_ += cons;
      totalPrev_ += prev;
      totalOrd_ += ord;

      if (stato === 'Da fatturare') {
        daFatturareSum_ += cons;
      } else if (stato === 'Fatturato') {
        fatturatoNonPagatoSum_ += cons;
      }

      // fatturato mensile (solo se dentro range)
      const refDate = o.createdAt || b.dataOdaFornitura || o.updatedAt;
      const d = refDate ? new Date(refDate) : null;
      if (d && !Number.isNaN(d.getTime())) {
        if (chartStart && chartEnd && d >= chartStart && d <= chartEnd) {
          const key = formatDateMonthKey(d.toISOString());
          if (!monthlyMap[key]) {
            monthlyMap[key] = {
              monthKey: key,
              consuntivo: 0,
              preventivo: 0,
              ordini: 0,
            };
          }
          monthlyMap[key].consuntivo += cons;
          monthlyMap[key].preventivo += prev;
          monthlyMap[key].ordini += ord;
        }
      }

      // distribuzione stato pagamento (su tutto il filtro)
      paymentMap[stato] = (paymentMap[stato] || 0) + cons;

      // top clienti per fatturato
      const cliente = o.companyName || 'Senza cliente';
      clientRevenueMap[cliente] =
        (clientRevenueMap[cliente] || 0) + cons;
      clientCountMap[cliente] = (clientCountMap[cliente] || 0) + 1;

      // margine: preventivo vs consuntivo
      const mKey = o.companyName || 'Senza cliente';
      if (!marginMap[mKey]) {
        marginMap[mKey] = { cliente: mKey, preventivo: 0, consuntivo: 0 };
      }
      marginMap[mKey].preventivo += prev;
      marginMap[mKey].consuntivo += cons;
    });

    const monthlyData_ = Object.values(monthlyMap)
      .sort((a, b) => (a.monthKey > b.monthKey ? 1 : -1))
      .map((m) => ({
        ...m,
        monthLabel: formatMonthLabel(m.monthKey),
      }));

    const paymentDistribution_ = Object.entries(paymentMap).map(
      ([name, value]) => ({
        name,
        value,
        color: PAYMENT_COLORS[name] || PAYMENT_COLORS.Altro,
      })
    );

    const topClientsRevenue_ = Object.entries(clientRevenueMap)
      .map(([cliente, value]) => ({
        cliente,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const topClientsCount_ = Object.entries(clientCountMap)
      .map(([cliente, count]) => ({ cliente, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const marginData_ = Object.values(marginMap)
      .map((r) => ({
        ...r,
        delta: r.preventivo - r.consuntivo,
      }))
      .sort((a, b) => b.consuntivo - a.consuntivo)
      .slice(0, 10);

    const countOrders_ = filteredOrders.length;
    const avgTicket_ =
      countOrders_ > 0 ? totalRevenue_ / countOrders_ : 0;

    const cashPotentialTotal_ = daFatturareSum_ + fatturatoNonPagatoSum_;

    return {
      totalRevenue: totalRevenue_,
      totalPreventivi: totalPrev_,
      totalOrdini: totalOrd_,
      countOrders: countOrders_,
      avgTicket: avgTicket_,
      monthlyData: monthlyData_,
      paymentDistribution: paymentDistribution_,
      topClientsRevenue: topClientsRevenue_,
      topClientsCount: topClientsCount_,
      marginData: marginData_,
      daFatturareSum: daFatturareSum_,
      fatturatoNonPagatoSum: fatturatoNonPagatoSum_,
      cashPotentialTotal: cashPotentialTotal_,
    };
  }, [filteredOrders, viewMode, yearFilter]);

  if (!canAccess) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* HEADER */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
              Dashboard contabile
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Panoramica su fatturato, clienti e stato dei pagamenti, basata
              sui dati inseriti in Gestione Fatturazione.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/gestione-fatturazione')}
              className="px-3 py-1.5 rounded-lg border border-emerald-500 text-emerald-700 text-xs font-semibold hover:bg-emerald-50"
            >
              Vai a Gestione Fatturazione
            </button>
          </div>
        </Card>

        {/* ERROR */}
        {error && (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* FILTRI GLOBALI */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row gap-4 md:items-end md:justify-between">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Anno
              </label>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                <option value="tutti">Tutti</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Cliente
              </label>
              <select
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                <option value="tutti">Tutti i clienti</option>
                {customerOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col items-stretch gap-2 md:items-end">
            <div className="inline-flex items-center bg-gray-100 rounded-full p-0.5 text-[11px]">
              <button
                type="button"
                onClick={() => setViewMode('12m')}
                className={`px-3 py-1 rounded-full ${
                  viewMode === '12m'
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-600'
                }`}
              >
                Ultimi 12 mesi
              </button>
              <button
                type="button"
                onClick={() => setViewMode('year')}
                className={`px-3 py-1 rounded-full ${
                  viewMode === 'year'
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-600'
                }`}
              >
                Anno solare
              </button>
            </div>
            <button
              onClick={loadData}
              className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              ⟳ Aggiorna dati
            </button>
          </div>
        </Card>

        {/* KPI CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Card className="shadow-sm px-3 py-3">
            <p className="text-[11px] text-gray-500 mb-1">
              Fatturato (consuntivo)
            </p>
            <p className="text-lg md:text-xl font-semibold text-gray-800">
              {formatCurrencyLabel(totalRevenue)}
            </p>
            <p className="text-[11px] text-emerald-600 mt-1">
              {countOrders} commesse
            </p>
          </Card>
          <Card className="shadow-sm px-3 py-3">
            <p className="text-[11px] text-gray-500 mb-1">
              Valore preventivi
            </p>
            <p className="text-lg md:text-xl font-semibold text-gray-800">
              {formatCurrencyLabel(totalPreventivi)}
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              Delta:{' '}
              <span
                className={
                  totalPreventivi - totalRevenue >= 0
                    ? 'text-emerald-600'
                    : 'text-red-600'
                }
              >
                {formatCurrencyLabel(totalPreventivi - totalRevenue)}
              </span>
            </p>
          </Card>
          <Card className="shadow-sm px-3 py-3">
            <p className="text-[11px] text-gray-500 mb-1">
              Ordini cliente (totale)
            </p>
            <p className="text-lg md:text-xl font-semibold text-gray-800">
              {formatCurrencyLabel(totalOrdini)}
            </p>
            <p className="text-[11px] text-gray-500 mt-1">
              Ticket medio:{' '}
              <span className="text-gray-800">
                {formatCurrencyLabel(avgTicket || 0)}
              </span>
            </p>
          </Card>
          <Card className="shadow-sm px-3 py-3">
            <p className="text-[11px] text-gray-500 mb-1">
              Cash potenziale (da incassare)
            </p>
            <p className="text-lg md:text-xl font-semibold text-gray-800">
              {formatCurrencyLabel(cashPotentialTotal)}
            </p>
            <p className="text-[11px] text-amber-600 mt-1">
              Da fatturare:{' '}
              <span className="font-semibold">
                {formatCurrencyLabel(daFatturareSum)}
              </span>
            </p>
            <p className="text-[11px] text-sky-600">
              Fatturato non pagato:{' '}
              <span className="font-semibold">
                {formatCurrencyLabel(fatturatoNonPagatoSum)}
              </span>
            </p>
          </Card>
        </div>

        {/* GRAFICI */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Andamento mensile */}
          <Card className="shadow-sm px-3 py-3 md:px-4 md:py-4 lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Andamento mensile fatturato
              </h2>
              <p className="text-[10px] text-gray-500">
                Consuntivo, preventivi e ordini
              </p>
            </div>
            {monthlyData.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nessun dato disponibile per il periodo selezionato.
              </p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="colorCons" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.7} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorPrev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.7} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorOrd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.7} />
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value) =>
                        formatCurrencyLabel(Number(value || 0))
                      }
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="consuntivo"
                      name="Consuntivo"
                      stroke="#22c55e"
                      fill="url(#colorCons)"
                    />
                    <Area
                      type="monotone"
                      dataKey="preventivo"
                      name="Preventivi"
                      stroke="#0ea5e9"
                      fill="url(#colorPrev)"
                    />
                    <Area
                      type="monotone"
                      dataKey="ordini"
                      name="Ordini cliente"
                      stroke="#f97316"
                      fill="url(#colorOrd)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Stato pagamenti */}
          <Card className="shadow-sm px-3 py-3 md:px-4 md:py-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Stato dei pagamenti
              </h2>
              <p className="text-[10px] text-gray-500">
                Ripartizione del fatturato per stato
              </p>
            </div>
            {paymentDistribution.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nessun dato disponibile per i filtri selezionati.
              </p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {paymentDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.color ||
                            PIE_COLORS[index % PIE_COLORS.length]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) =>
                        formatCurrencyLabel(Number(value || 0))
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* TOP CLIENTI & MARGINI */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top clienti per fatturato */}
          <Card className="shadow-sm px-3 py-3 md:px-4 md:py-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Top clienti per fatturato
              </h2>
            <p className="text-[10px] text-gray-500">
                Valore totale consuntivo per cliente
              </p>
            </div>
            {topClientsRevenue.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nessun dato disponibile per i filtri selezionati.
              </p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topClientsRevenue}
                    layout="vertical"
                    margin={{ left: 80, right: 10, top: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="cliente"
                      width={120}
                      tick={{ fontSize: 10 }}
                    />
                    <Tooltip
                      formatter={(value) =>
                        formatCurrencyLabel(Number(value || 0))
                      }
                    />
                    <Bar dataKey="value" name="Consuntivo" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Margine preventivo vs consuntivo */}
          <Card className="shadow-sm px-3 py-3 md:px-4 md:py-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-800">
                Coerenza preventivi vs consuntivi
              </h2>
              <p className="text-[10px] text-gray-500">
                Prime 10 aziende per volume, differenza tra preventivo e
                consuntivo
              </p>
            </div>
            {marginData.length === 0 ? (
              <p className="text-xs text-gray-500">
                Nessun dato disponibile per i filtri selezionati.
              </p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={marginData}
                    layout="vertical"
                    margin={{ left: 80, right: 10, top: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="cliente"
                      width={120}
                      tick={{ fontSize: 10 }}
                    />
                    <Tooltip
                      formatter={(value) =>
                        formatCurrencyLabel(Number(value || 0))
                      }
                    />
                    <Legend />
                    <Bar
                      dataKey="preventivo"
                      name="Preventivo"
                      stackId="a"
                      fill="#0ea5e9"
                    />
                    <Bar
                      dataKey="consuntivo"
                      name="Consuntivo"
                      stackId="a"
                      fill="#22c55e"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* TOP CLIENTI PER NUMERO COMMESSE */}
        <Card className="shadow-sm px-3 py-3 md:px-4 md:py-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-800">
              Top clienti per numero commesse
            </h2>
            <p className="text-[10px] text-gray-500">
              Utile per capire chi assorbe più interventi
            </p>
          </div>
          {topClientsCount.length === 0 ? (
            <p className="text-xs text-gray-500">
              Nessun dato disponibile per i filtri selezionati.
            </p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topClientsCount}
                  margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="cliente" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="N. commesse" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DashboardContabile;
